// // composables/useServerRequest.ts
// import { useRuntimeConfig, useRequestHeaders, useAsyncData } from '#imports'
// import { toRaw } from 'vue'
// import type { ApiResponse } from '~/types'

// export default function useServerRequest<T = any>(
//   key: string,
//   url: string,
//   options?: {
//     method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
//     params?: Record<string, any>
//     body?: Record<string, any>
//     headers?: HeadersInit
//     immediate?: boolean
//     server?: boolean // 控制服务端执行
//     // 可选：手动指定语言，优先级高于当前 i18n 语言
//     language?: string
//   }
// ) {
//   const config = useRuntimeConfig()
//   const headersFromRequest = useRequestHeaders(['authorization']) || {}
//   const method = options?.method?.toUpperCase() ?? 'GET'
//   const isPostLike = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)

//   // 处理语言格式（可选：统一为短格式，如 zh-CN → zh，en-US → en）
//   const getLanguageHeader = () => {
//     // 优先使用手动传入的 language，其次用 i18n 当前语言
//     const lang = options?.language || 'zh-CN'
//     // 格式适配：若接口要求短语言码，可取消注释
//     // return lang.split('-')[0] 
//     return lang // 保持原格式（如 zh-CN）
//   }

//   // 解包 ref / reactive，取到真实的值
//   const unwrap = (input?: Record<string, any>) => {
//     if (!input) return undefined
//     try {
//       return JSON.parse(JSON.stringify(toRaw(input)))
//     } catch {
//       return input
//     }
//   }

//   // 拼接 dynamicKey，让 useAsyncData 每次都识别为不同请求
//   const rawParams = unwrap(options?.params)
//   const rawBody = unwrap(options?.body)

//   const dynamicKey = `${key}-${JSON.stringify({
//     method,
//     query: !isPostLike ? rawParams : undefined,
//     body: isPostLike ? rawBody ?? rawParams : rawBody,
//     language: getLanguageHeader() // 新增：语言加入 dynamicKey，避免缓存
//   })}`
  

//   return useAsyncData<ApiResponse<T>>(
//     dynamicKey,
//     () =>
//       $fetch(url, {
//         baseURL:config?.public?.baseURL,
//         method,
//         query: !isPostLike ? rawParams : undefined,
//         body: isPostLike ? rawBody ?? rawParams : rawBody,
//         headers: {
//           ...headersFromRequest,
//           ...options?.headers,
//           // ******** 核心：添加 x-Language 头 ********
//           'x-Language': getLanguageHeader(),
//           // 补充默认 Content-Type
//           'Content-Type': 'application/json'
//         },
//       }),
//     {
//       immediate: options?.immediate !== false,
//       server: options?.server ?? true, // 核心修改：默认服务器端执行
//     }
//   )
// }

// composables/useServerRequest.ts
import { useRuntimeConfig, useRequestHeaders, useAsyncData } from '#imports'
import { toRaw } from 'vue'
import type { ApiResponse } from '~/types'
import { resolveApiBaseURL } from '~/utils/apiBase'

export default function useServerRequest<T = any>(
  key: string,
  url: string,
  options?: {
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
    params?: Record<string, any>
    body?: Record<string, any>
    headers?: HeadersInit
    immediate?: boolean
    server?: boolean // 控制服务端执行
    // 可选：手动指定语言，优先级高于当前 i18n 语言
    language?: string
    // 新增：超时时间配置（毫秒）
    timeout?: number
  }
) {
  const config = useRuntimeConfig()
  const headersFromRequest = useRequestHeaders(['authorization']) || {}
  const method = options?.method?.toUpperCase() ?? 'GET'
  const isPostLike = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)
  // 默认超时时间：10秒
  const timeout = options?.timeout ?? 10000

  // 处理语言格式（可选：统一为短格式，如 zh-CN → zh，en-US → en）
  const getLanguageHeader = () => {
    // 优先使用手动传入的 language，其次用 i18n 当前语言
    const lang = options?.language || 'zh-CN'
    // 格式适配：若接口要求短语言码，可取消注释
    // return lang.split('-')[0] 
    return lang // 保持原格式（如 zh-CN）
  }

  // 解包 ref / reactive，取到真实的值
  const unwrap = (input?: Record<string, any>) => {
    if (!input) return undefined
    if (typeof FormData !== 'undefined' && input instanceof FormData) {
      return input
    }
    try {
      return JSON.parse(JSON.stringify(toRaw(input)))
    } catch {
      return input
    }
  }

  // 拼接 dynamicKey，让 useAsyncData 每次都识别为不同请求
  const rawParams = unwrap(options?.params)
  const rawBody = unwrap(options?.body)

  const dynamicKey = `${key}-${JSON.stringify({
    method,
    query: !isPostLike ? rawParams : undefined,
    body: isPostLike ? rawBody ?? rawParams : rawBody,
    language: getLanguageHeader(),
    timeout // 超时时间加入 dynamicKey，确保不同超时配置的请求不缓存冲突
  })}`

  return useAsyncData<ApiResponse<T>>(
    dynamicKey,
    async () => {
      // 创建 AbortController 用于超时中断
      const controller = new AbortController()
      const signal = controller.signal

      // 设置超时定时器
      const timeoutId = setTimeout(() => {
        controller.abort()
        throw new Error(`请求超时（${timeout}ms）: ${url}`)
      }, timeout)

      try {
        // 发起请求
        const body = isPostLike ? rawBody ?? rawParams : rawBody
        const headers = {
          'Content-Type': 'application/json',
          ...headersFromRequest,
          ...options?.headers,
          // 核心：添加 x-Language 头
          'x-Language': getLanguageHeader(),
        }

        if (typeof FormData !== 'undefined' && body instanceof FormData) {
          delete (headers as any)['Content-Type']
        }

        const response = await $fetch<ApiResponse<T>>(url, {
          baseURL: resolveApiBaseURL(config?.public?.baseURL, config?.apiBase),
          method,
          query: !isPostLike ? rawParams : undefined,
          body,
          headers,
          // 传递 abort signal
          signal,
          // 客户端超时配置（nuxt 的 $fetch 内置超时）
          timeout
        })

        return response
      } catch (error:any) {
        // 处理超时错误
        if (error.name === 'AbortError' || error.message.includes('超时')) {
          throw new Error(`请求超时：无法在${timeout}毫秒内完成请求，请检查网络或稍后重试`)
        }
        // 重新抛出其他错误
        throw error
      } finally {
        // 清除超时定时器
        clearTimeout(timeoutId)
      }
    },
    {
      immediate: options?.immediate !== false,
      server: options?.server ?? true, // 核心修改：默认服务器端执行
    }
  )
}